import { wcagContrast, wcagLuminance } from 'culori';

/** WCAG 2.x rating for normal-size body text at a given contrast ratio. */
export type ContrastRating = 'AAA' | 'AA' | 'AA Large' | 'Fail';

/** Relative luminance (0–1) per WCAG; thin wrapper over culori. */
export function luminance(color: string): number {
  return wcagLuminance(color);
}

/** Contrast ratio (1–21) between two colors per WCAG; thin wrapper over culori. */
export function contrastRatio(a: string, b: string): number {
  return wcagContrast(a, b);
}

/** Pick black or white text — whichever is more legible on `bg`. */
export function legibleTextColor(bg: string): '#000000' | '#ffffff' {
  return contrastRatio(bg, '#000000') >= contrastRatio(bg, '#ffffff') ? '#000000' : '#ffffff';
}

/**
 * WCAG rating for normal text. Thresholds: AAA ≥ 7, AA ≥ 4.5, AA Large ≥ 3
 * (passes only for large/bold text), otherwise Fail.
 */
export function rateContrast(ratio: number): ContrastRating {
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'AA Large';
  return 'Fail';
}

export interface ContrastInfo {
  /** The more legible of black/white text on the swatch. */
  textColor: '#000000' | '#ffffff';
  ratio: number;
  rating: ContrastRating;
}

/**
 * For a swatch background, the best legible text color and its WCAG rating —
 * the contrast hint shown on each harmony color.
 */
export function swatchContrast(bg: string): ContrastInfo {
  const textColor = legibleTextColor(bg);
  const ratio = contrastRatio(bg, textColor);
  return { textColor, ratio, rating: rateContrast(ratio) };
}
